import Reorder2D, {
  ItemInfo as ReorderItem,
} from "@components/global/Reorder2Dv2";
import { ReorderInfo } from "@components/global/Reorder2Dv2/matrix";
import { useConnectionsActions3 } from "@mobx/hooks/useConnectionsActions3";
import { formatRackDisplayName3 } from "@mobx/store/connections";
import mainLocationsStore from "@mobx/store/locations";
import { DRAGGING_TYPES3, IRackTemplate, isPostitionedRackTable3 } from "@mobx/store/locations/types";
import cn from "classnames";
import { AnimatePresence } from "framer-motion";
import { observer } from "mobx-react-lite";
import { DragEventHandler, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { RACK_UNIT } from "../../../config";
import Rack3 from "../Rack3";
import styles from "./RackReorderGroup.module.scss";

export interface IRackReorderGroup3Props {
  readonly lock: boolean;
  readonly disabled: boolean;
  readonly validRacks?: boolean;

  onDragOver: DragEventHandler<HTMLDivElement>;
  onDragEnter: DragEventHandler<HTMLDivElement>;
  onDragLeave: DragEventHandler<HTMLDivElement>;
  onNewRackSaved(): void;
}

const rackTypeToItemSize = ([x0, x1]: [number, number]): [number, number] => [
  x1,
  x0 * 2 + 2,
]; // каждый столбец это 2 юнита и по одному юниту на паддинг с каждой стороны

const rackRows = (typ: [number, number]): number => rackTypeToItemSize(typ)[0];
const rackCols = (typ: [number, number]): number => rackTypeToItemSize(typ)[1];

const useControl = (props: IRackReorderGroup3Props) => {
  const connActions = useConnectionsActions3();
  const locationStore = mainLocationsStore.activeLocation!;
  const buildingStore = locationStore.activeBuilding!;
  const racks = useMemo(
    () => [...buildingStore.racks.values(), ...buildingStore.temporaryRacks.map(r => r.rack)],
    [buildingStore.racks, buildingStore.temporaryRacks],
  );
  const found = useMemo(() =>
    [...buildingStore.racks.values()].map((r) => `${r.id}`),
    [buildingStore.racks]
  );

  useEffect(() => {
    if (props.validRacks) connActions?.mergeRacks(racks as any[]);
  }, [racks, props.validRacks]);

  const items: ReorderItem[] = useMemo(
    () =>
      racks.map((rack) => ({
        numCols: rackCols(rack.rack_type),
        rows: rackRows(rack.rack_type),
        id: `${rack.id}`,
        userInfo: {
          ...rack,
          rackDisplayName: formatRackDisplayName3(
            locationStore.location.Code,
            buildingStore.building.name,
            rack.rack_id
          ),
        },
        pos: isPostitionedRackTable3(rack) ? rack.rack_pos : undefined,
        render: (
          <div
            className={cn(styles.wrapper, {})}
            key={rack.id}
          >
            <Rack3 rack={rack} />
          </div>
        ),
      })),
    [buildingStore.building.name, locationStore.location.Code, racks]
  );

  const align = useCallback(
    (info?: ReorderInfo[]) => {
      info !== undefined && buildingStore.updateRacksPositions(info);
    },
    []
  );

  return {
    items,
    hasSearch: !!buildingStore?.hasSearch,
    found,
    buildingStore,
    align,
    extractTransferItemSize(ev: React.DragEvent) {
      const s = ev.dataTransfer.getData(DRAGGING_TYPES3.AVAILABLE_RACK).trim();
      if (!s.length) return [1, 4] as [number, number];
      const d = JSON.parse(s) as IRackTemplate;
      return rackTypeToItemSize(d.rack_type);
    },
  };
};

const RackReorderGroup3 = observer((props: IRackReorderGroup3Props) => {
  const ctrl = useControl(props);
  const counter = useRef(0);
  const scrollTargetRef = useRef<HTMLDivElement | null>(null);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [_, redraw] = useState(false);

  const onScroll = useCallback(() => {
    if (!scrollTargetRef.current) return;
    const left = scrollTargetRef.current.scrollLeft;
    setTimeout(() => setScrollLeft(left), 10);
  }, [scrollTargetRef.current]);

  if (!scrollTargetRef.current) {
    setTimeout(() => redraw(x => !x), 100);
  }

  useEffect(() => {
    if (scrollTargetRef.current) {
      scrollTargetRef.current.scrollLeft = 0;
      scrollTargetRef.current.scrollTop = 0;
    }
  }, [mainLocationsStore.activeLocation?.activeBuilding]);

  return (
    <AnimatePresence initial={false}>
      <div
        className={cn(styles.groupWrapper, {
          [styles.disabled]: props.disabled,
        })}
        ref={scrollTargetRef}
        onScroll={onScroll}
      >
        <Reorder2D.Group
          onReorder={info => ctrl.buildingStore.reorderRacks(info, props.onNewRackSaved)}
          className={styles.group}
          rowClassName={styles.row}
          items={ctrl.items}
          minColumns={500}
          unlocked={!props.lock}
          dragType={DRAGGING_TYPES3.AVAILABLE_RACK}
          unit={RACK_UNIT}
          extractTransferItemSize={ctrl.extractTransferItemSize}
          scrollX={scrollLeft}
          onAlign={ctrl.align}
          onDragLeave={(e) => {
            if (counter.current === 0)
              props.onDragLeave(e);
          }}
          onDrop={(e) => {
            const type = e.dataTransfer.items[0]?.type;
            if (type !== DRAGGING_TYPES3.AVAILABLE_RACK) return;
        
            const data = JSON.parse(
              e.dataTransfer.getData(DRAGGING_TYPES3.AVAILABLE_RACK)
            ) as IRackTemplate;

            const [width, height] = data.rack_type;
            ctrl.buildingStore.createRackLocally(width, height, `${data.id}`);
          }}
          onDragOver={props.onDragOver}
          onDragEnter={props.onDragEnter}
          hasSearch={ctrl.hasSearch}
          found={new Set(ctrl.found)}
          scrollTarget={scrollTargetRef.current || undefined}
        />
      </div>
    </AnimatePresence>
  );
});

RackReorderGroup3.displayName = "RackReorderGroup3";

export default RackReorderGroup3;
